package org.teddapp.project.sync

import com.tedd.db.TeddDatabase

/**
 * Moteur de synchronisation local ↔ distant.
 *
 * Sémantique LWW (last-write-wins) :
 *  - gagnant = `updated_at` le plus grand (ligne entière, pas par champ) ;
 *  - en cas d'égalité : remote gagne (tie-break) ;
 *  - tombstone réveillé : si tombstone local à T et update remote à T+1,
 *    la ligne est restaurée avec les valeurs remote (LWW strict).
 *
 * Soft-delete propagé : un `is_deleted = 1` local devient un tombstone push ;
 * un tombstone remote devient un `is_deleted = 1` local.
 */
interface SyncEngine {
    /** Push puis pull, dans l'ordre topologique des entités. */
    suspend fun sync(): SyncResult

    /** Push uniquement — utile pour tests et pour un mode "envoyer mes modifs" rapide. */
    suspend fun pushOnly(): SyncResult

    /** Pull uniquement — utile pour tests et pour un mode "rafraîchir" rapide. */
    suspend fun pullOnly(): SyncResult

    /**
     * Push puis pull d'un sous-ensemble d'entités.
     *
     * C'est le cycle court : ce qui doit circuler en quelques secondes, comme le
     * chronomètre partagé, sans payer les vingt-six tables à chaque battement.
     */
    suspend fun syncOnly(entities: List<EntityKind>): SyncResult

    /** Nombre de lignes d'une table côté distant, ou null si indisponible. */
    suspend fun remoteCount(entity: EntityKind): Long?
}

/**
 * Implémentation SQLDelight du moteur de sync.
 *
 * Algorithme :
 *  - **push** : pour chaque entité (ordre topologique pushOrder), `selectDirty()` →
 *    convertir en `RemoteRow` → `remote.push()` → `markSynced(updatedAt)`. Si le push
 *    échoue, la ligne reste dirty et sera retentée au prochain push.
 *  - **pull** : pour chaque entité (ordre topologique pullOrder), récupérer le cursor →
 *    `remote.pull(since=cursor)` → pour chaque ligne, comparer LWW vs local et appliquer
 *    si distant gagne → avancer le cursor à `max(updatedAt)` reçu.
 *  - **sync** : push puis pull, table par table ; un échec n'arrête ni les autres tables
 *    ni le pull, il est rendu à la fin (retour Failure avec le détail).
 *
 * Garde anti-boucle : applyRemote écrit directement le `updated_at` du remote dans la
 * ligne, puis markSynced fixe `synced_at = remote.updatedAt`. Comme `selectDirty` exige
 * `synced_at < updated_at`, la ligne appliquée n'est pas re-poussée.
 */
class SqlDelightSyncEngine(
    private val db: TeddDatabase,
    private val remote: RemoteStore,
    private val clock: Clock,
) : SyncEngine {

    companion object {
        /** Lignes par requête d'envoi. Mesuré : deux cents tâches en quatre secondes, cinq cents en dix. */
        const val PUSH_CHUNK: Int = 200

        /**
         * Poids visé d'une tranche. Le serveur refuse au-delà de 2 Mo ; une note qui porte
         * un dessin en pèse plusieurs centaines de kilo-octets, deux cents ne passeraient pas.
         */
        const val PUSH_MAX_BYTES: Int = 1_200_000

        /**
         * Découpe en tranches de [PUSH_CHUNK] lignes au plus, et d'environ [PUSH_MAX_BYTES].
         * Une ligne plus lourde que la limite part seule. Une ligne supprimée compte double :
         * elle voyage en deux mutations.
         */
        internal fun pushChunks(rows: List<RemoteRow>): List<List<RemoteRow>> {
            val chunks = mutableListOf<List<RemoteRow>>()
            var current = mutableListOf<RemoteRow>()
            var bytes = 0
            for (row in rows) {
                val weight = weightOf(row)
                if (current.isNotEmpty() && (current.size >= PUSH_CHUNK || bytes + weight > PUSH_MAX_BYTES)) {
                    chunks += current
                    current = mutableListOf()
                    bytes = 0
                }
                current += row
                bytes += weight
            }
            if (current.isNotEmpty()) chunks += current
            return chunks
        }

        private fun weightOf(row: RemoteRow): Int {
            val fields = row.payload.values.sumOf { (it?.toString()?.length ?: 4) + 16 } + 160
            return if (row.isDeleted) fields * 2 else fields
        }

        /** Clé du curseur unique d'un distant qui tire toutes les entités d'un coup. */
        const val GLOBAL_CURSOR: String = "__server"
    }

    override suspend fun sync(): SyncResult {
        // Un envoi qui échoue sur une table ne doit pas priver l'appareil de ce que les
        // autres ont écrit : on tire quand même, puis on rend tous les échecs.
        val failures = pushCollecting(EntityKind.pushOrder) + pullCollecting(EntityKind.pullOrder)
        return failuresToResult(failures)
    }

    override suspend fun syncOnly(entities: List<EntityKind>): SyncResult {
        val failures = pushCollecting(entities) + pullCollecting(entities)
        return failuresToResult(failures)
    }

    override suspend fun pushOnly(): SyncResult = pushEntities(EntityKind.pushOrder)

    override suspend fun pullOnly(): SyncResult = pullEntities(EntityKind.pullOrder)

    /** Un échec, table par table, pour le compte rendu du cycle. */
    private data class EntityFailure(val entity: EntityKind, val cause: Throwable, val rows: Int)

    private fun failuresToResult(failures: List<EntityFailure>): SyncResult {
        if (failures.isEmpty()) return SyncResult.Success
        val first = failures.first()
        val structured = failures.map { f ->
            val why = f.cause.message?.lineSequence()?.firstOrNull()?.take(80) ?: (f.cause::class.simpleName ?: "erreur")
            SyncEntityFailure(f.entity, f.rows, why)
        }
        val summary = structured.joinToString(" · ") { "${it.entity.tableName} (${it.rows} lignes) : ${it.message}" }
        return SyncResult.Failure(first.entity, first.cause, summary, structured)
    }

    override suspend fun remoteCount(entity: EntityKind): Long? = remote.count(entity)

    private suspend fun pushEntities(order: List<EntityKind>): SyncResult =
        failuresToResult(pushCollecting(order))

    /**
     * Pousse chaque table, par tranches de [PUSH_CHUNK] lignes, les plus anciennes
     * d'abord. Une tranche acceptée est marquée synchronisée tout de suite : si la
     * suivante échoue, ce qui est passé reste passé. Une table en échec n'arrête pas
     * les autres ; l'échec est rendu à la fin.
     *
     * Pourquoi des tranches : un parcours importé fait six mille tâches, soit quatre
     * mégaoctets d'un coup, que le serveur ne traite pas dans le délai. Deux cents
     * lignes passent en quelques secondes.
     */
    private suspend fun pushCollecting(order: List<EntityKind>): List<EntityFailure> {
        val failures = mutableListOf<EntityFailure>()
        for (entity in order) {
            val dirty = SyncEntityOps.selectDirty(entity, db).sortedBy { it.updatedAt }
            if (dirty.isEmpty()) continue
            try {
                for (chunk in pushChunks(dirty)) {
                    remote.push(entity, chunk)
                    val syncedAt = clock.now()
                    db.transaction { for (row in chunk) SyncEntityOps.markSynced(entity, db, row.id, row.updatedAt, syncedAt) }
                }
            } catch (e: Throwable) {
                if (e is kotlinx.coroutines.CancellationException) throw e
                failures += EntityFailure(entity, e, dirty.size)
            }
        }
        return failures
    }

    private suspend fun pullEntities(order: List<EntityKind>): SyncResult =
        failuresToResult(pullCollecting(order))

    /**
     * Tire chaque table depuis son curseur. Le curseur n'avance qu'une fois les lignes
     * appliquées : un échec le laisse où il était, et la table sera retentée. Une
     * table en échec n'arrête pas les autres.
     */
    private suspend fun pullCollecting(order: List<EntityKind>): List<EntityFailure> {
        val failures = mutableListOf<EntityFailure>()
        // Distant à curseur unique : on tire tout d'un coup, puis on applique dans l'ordre
        // des parents. Le curseur n'avance que si tout a été appliqué.
        val since = db.syncCursorQueries.getCursor(GLOBAL_CURSOR).executeAsOneOrNull() ?: 0L
        val global = try {
            remote.pullAll(since)
        } catch (e: Throwable) {
            if (e is kotlinx.coroutines.CancellationException) throw e
            return listOf(EntityFailure(order.firstOrNull() ?: EntityKind.UserProfile, e, 0))
        }
        if (global != null) {
            val now = clock.now()
            for (entity in EntityKind.pullOrder) {
                val rows = global.rows[entity].orEmpty()
                try {
                    // Une table d'un coup : validée ligne par ligne, chaque écriture attend le
                    // disque, et trente mille tâches prennent une demi-heure sur une montre.
                    db.transaction { for (remoteRow in rows) applyOne(entity, remoteRow, now) }
                } catch (e: Throwable) {
                    if (e is kotlinx.coroutines.CancellationException) throw e
                    failures += EntityFailure(entity, e, rows.size)
                }
            }
            if (failures.isEmpty() && global.cursor != since) db.syncCursorQueries.upsertCursor(GLOBAL_CURSOR, global.cursor)
            return failures
        }
        for (entity in order) {
            var since = db.syncCursorQueries.getCursor(entity.tableName).executeAsOneOrNull() ?: 0L
            try {
                // Page après page jusqu'à une page incomplète : le curseur avance à chaque page
                // reçue, donc une coupure au milieu ne fait rien perdre ni rien relire.
                while (true) {
                    val rows: List<RemoteRow> = remote.pull(entity, since)
                    if (rows.isEmpty()) break
                    val now = clock.now()
                    db.transaction { for (remoteRow in rows) applyOne(entity, remoteRow, now) }
                    // Le numéro d'arrivée du serveur fait foi ; sans lui, l'ancienne date de modification.
                    val byArrival = rows.all { it.receivedSeq != null }
                    val newCursor = if (byArrival) rows.maxOf { it.receivedSeq!! } else rows.maxOf { it.updatedAt }
                    val effectiveSince = if (byArrival && since > LEGACY_CURSOR_THRESHOLD) 0L else since
                    if (newCursor > effectiveSince) {
                        db.syncCursorQueries.upsertCursor(entity.tableName, newCursor)
                        since = newCursor
                    } else {
                        break
                    }
                    val page = remote.pullPageSize
                    if (!byArrival || page == null || rows.size < page) break
                }
            } catch (e: Throwable) {
                if (e is kotlinx.coroutines.CancellationException) throw e
                failures += EntityFailure(entity, e, 0)
            }
        }
        return failures
    }

    /**
     * Applique une ligne distante selon LWW :
     *  - local plus récent (strictement) → on ignore (le push prochain propagera la version locale).
     *  - sinon (égalité ou distant plus récent) → on applique, et on marque synced à `remote.updatedAt`.
     */
    private fun applyOne(entity: EntityKind, remoteRow: RemoteRow, now: Long) {
        val local = SyncEntityOps.selectByIdAnyState(entity, db, remoteRow.id)
        if (local != null && local.updatedAt > remoteRow.updatedAt) {
            // LWW : local strictement plus récent — on garde le local et on n'avance pas.
            return
        }
        SyncEntityOps.applyRemote(entity, db, remoteRow)
        // Marquer la ligne comme déjà synced à updatedAt = remote.updatedAt
        // (anti-boucle : selectDirty ne la reprendra pas).
        SyncEntityOps.markSynced(entity, db, remoteRow.id, remoteRow.updatedAt, remoteRow.updatedAt)
    }
}
